"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = SignInGate;
var _react = _interopRequireWildcard(require("react"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
/**
 * SignInGate — the sign-in screen shown when an app has no live session.
 *
 * Designer and Explorer used to render a diagnostic card here: a host/reason
 * dump and a "Go to Console" button. That told an author whose session had
 * simply timed out to go somewhere else and figure it out. This signs them
 * back in where they are.
 *
 * Sign-in only. Registration, onboarding and passkey enrolment stay in the
 * Console — an author reaching this screen already has an account; they just
 * stopped having a session.
 *
 * The Hanko instance is injected rather than imported so this package takes no
 * dependency on the auth SDK, and each app keeps its own configured client:
 *
 *   <SignInGate hanko={hanko} logo={<SamLogo size={64} showWordmark />}
 *               onSignedIn={refreshUser} />
 *
 * `onSignedIn` should re-run the app's own session check (refreshUser), not
 * reload the page — a reload would discard unsaved work, which is exactly what
 * an author hits this screen holding.
 */function SignInGate({
  hanko,
  logo,
  appName,
  onSignedIn,
  detail
}) {
  const [email, setEmail] = (0, _react.useState)('');
  const [password, setPassword] = (0, _react.useState)('');
  const [totpCode, setTotpCode] = (0, _react.useState)('');
  const [error, setError] = (0, _react.useState)('');
  const [loading, setLoading] = (0, _react.useState)(false);
  const [flowState, setFlowState] = (0, _react.useState)(null);

  // Flow handlers run outside React's render cycle, so current field values
  // reach them through refs rather than closed-over state.
  const emailRef = (0, _react.useRef)('');
  const passwordRef = (0, _react.useRef)('');
  const totpRef = (0, _react.useRef)('');
  const handlersRef = (0, _react.useRef)(null);
  const initialized = (0, _react.useRef)(false);
  (0, _react.useEffect)(() => {
    emailRef.current = email;
  }, [email]);
  (0, _react.useEffect)(() => {
    passwordRef.current = password;
  }, [password]);
  (0, _react.useEffect)(() => {
    totpRef.current = totpCode;
  }, [totpCode]);
  const showState = (0, _react.useCallback)(state => {
    if (state?.error?.message) setError(state.error.message);
    setFlowState(state);
    setLoading(false);
  }, []);
  const processFlowResult = (0, _react.useCallback)(async result => {
    if (!result || !result.name) {
      setLoading(false);
      return;
    }
    const handlers = handlersRef.current;
    if (handlers && handlers[result.name]) {
      const next = await handlers[result.name](result);
      if (next && next.name) await processFlowResult(next);
    } else {
      showState(result);
    }
  }, [showState]);
  const startLoginFlow = (0, _react.useCallback)(() => {
    if (!hanko) return;
    setLoading(false);
    setError('');
    setFlowState(null);
    const preflight = async state => {
      const available = !!window.PublicKeyCredential;
      let platform = false;
      let conditional = false;
      try {
        platform = available && (await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());
      } catch (_) {/* capability probe only */}
      try {
        conditional = available && window.PublicKeyCredential.isConditionalMediationAvailable && (await window.PublicKeyCredential.isConditionalMediationAvailable());
      } catch (_) {/* capability probe only */}
      return state.actions.register_client_capabilities({
        webauthn_available: available,
        webauthn_platform_authenticator_available: platform,
        webauthn_conditional_mediation_available: conditional
      }).run();
    };
    const skipOr = state => state.actions?.skip ? state.actions.skip().run() : showState(state);
    const handlers = {
      onError: err => {
        setError(err?.message || 'Sign-in failed. Please try again.');
        setLoading(false);
      },
      preflight,
      login_init: state => {
        showState(state);
      },
      login_method_chooser: state => state.actions?.continue_to_password_login ? state.actions.continue_to_password_login().run() : showState(state),
      login_password: state => passwordRef.current ? state.actions.password_login({
        password: passwordRef.current
      }).run() : showState(state),
      login_passkey: state => showState(state),
      login_otp: state => {
        if (totpRef.current) {
          const action = state.actions?.otp_code_verify || state.actions?.otp_code_validate;
          if (action) return action({
            otp_code: totpRef.current
          }).run();
        }
        return showState(state);
      },
      passcode_confirmation: state => totpRef.current ? state.actions.verify_passcode({
        code: totpRef.current
      }).run() : showState(state),
      login_password_recovery: state => showState(state),
      login_security_key: state => showState(state),
      // Onboarding belongs to the Console; anyone here is already enrolled.
      credential_onboarding_chooser: skipOr,
      onboarding_create_passkey: skipOr,
      onboarding_email: skipOr,
      onboarding_username: skipOr,
      device_trust: skipOr,
      success: async () => {
        // The cookie lands slightly after the flow reports success; without
        // this wait the app's session re-check races it and bounces straight
        // back to this screen.
        for (let i = 0; i < 15; i++) {
          if (hanko.session?.isValid?.()) break;
          await new Promise(r => setTimeout(r, 200));
        }
        setLoading(false);
        if (onSignedIn) onSignedIn();
      },
      error: state => showState(state)
    };
    handlersRef.current = handlers;
    hanko.flow.init('/login', handlers);
  }, [hanko, showState, onSignedIn]);
  (0, _react.useEffect)(() => {
    if (initialized.current) return;
    initialized.current = true;
    startLoginFlow();
  }, [startLoginFlow]);
  const submit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const state = flowState;
      if (!state) {
        startLoginFlow();
        return;
      }
      let result = null;
      if (state.name === 'login_init' && state.actions?.continue_with_login_identifier) {
        result = await state.actions.continue_with_login_identifier({
          email: emailRef.current
        }).run();
      } else if (state.name === 'login_password' && state.actions?.password_login) {
        result = await state.actions.password_login({
          password: passwordRef.current
        }).run();
      } else if (state.name === 'login_otp') {
        const action = state.actions?.otp_code_verify || state.actions?.otp_code_validate;
        if (action) result = await action({
          otp_code: totpRef.current
        }).run();
      } else if (state.name === 'passcode_confirmation' && state.actions?.verify_passcode) {
        result = await state.actions.verify_passcode({
          code: totpRef.current
        }).run();
      }
      if (result) await processFlowResult(result);
    } catch (err) {
      setError(err?.message || 'Sign-in failed. Please try again.');
      setLoading(false);
    }
  };
  const stateName = flowState?.name;
  const wantsCode = stateName === 'login_otp' || stateName === 'passcode_confirmation';
  const wantsPassword = stateName === 'login_password';
  return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    className: "signin-gate",
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: "signin-gate__card",
      children: [logo && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        className: "signin-gate__logo",
        children: logo
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("h1", {
        className: "signin-gate__title",
        children: wantsCode ? 'Enter your verification code' : 'Your session has expired'
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "signin-gate__body",
        children: wantsCode ? 'Enter the code from your authenticator app.' : `Sign in to continue${appName ? ` in the ${appName}` : ''}. Your work stays open in this tab.`
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("form", {
        onSubmit: submit,
        className: "signin-gate__form",
        children: [wantsCode ? /*#__PURE__*/(0, _jsxRuntime.jsxs)("label", {
          className: "signin-gate__field",
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
            children: "Authentication code"
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("input", {
            type: "text",
            inputMode: "numeric",
            autoComplete: "one-time-code",
            autoFocus: true,
            value: totpCode,
            onChange: e => setTotpCode(e.target.value),
            disabled: loading,
            required: true
          })]
        }) : /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
          children: [!wantsPassword && /*#__PURE__*/(0, _jsxRuntime.jsxs)("label", {
            className: "signin-gate__field",
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
              children: "Email"
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)("input", {
              type: "email",
              autoComplete: "username",
              autoFocus: true,
              value: email,
              onChange: e => setEmail(e.target.value),
              disabled: loading,
              required: true
            })]
          }), wantsPassword && /*#__PURE__*/(0, _jsxRuntime.jsxs)("label", {
            className: "signin-gate__field",
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
              children: "Password"
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)("input", {
              type: "password",
              autoComplete: "current-password",
              autoFocus: true,
              value: password,
              onChange: e => setPassword(e.target.value),
              disabled: loading,
              required: true
            })]
          })]
        }), error && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
          className: "signin-gate__error",
          role: "alert",
          children: error
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
          type: "submit",
          className: "signin-gate__submit",
          disabled: loading,
          children: loading ? 'Signing in…' : wantsCode ? 'Verify' : wantsPassword ? 'Sign in' : 'Continue'
        })]
      }), detail && /*#__PURE__*/(0, _jsxRuntime.jsxs)("details", {
        className: "signin-gate__detail",
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("summary", {
          children: "Technical details"
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("pre", {
          children: detail
        })]
      })]
    })
  });
}